import React from 'react';
import { Button, Form, FormField, Input, Message, Segment } from 'semantic-ui-react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getWithAuth, postWithAuth } from 'src/api';
import './DownloadConfig.module.css';

type DownloadConfigValues = {
  ova_key: string;
  lpupdate_key: string;
};

export function DownloadConfig() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<DownloadConfigValues>({
    queryKey: ['admin', 'downloadConfig'],
    queryFn: () => getWithAuth('admin/download_config'),
  });

  const DEFAULT_OVA_KEY =
    's3://on-prem-linkplanner/build_output/linkplanner_onprem_1aab2e7931/output-lp-app/linkplanner_onprem_1aab2e7931.ova';
  const DEFAULT_LPUPDATE_KEY =
    's3://on-prem-linkplanner/build_output/linkplanner_onprem_1aab2e7931/output-lp-app/linkplanner-update-1aab2e7931.lpupdate';

  const { control, handleSubmit, reset } = useForm<DownloadConfigValues>({
    values: data ?? { ova_key: DEFAULT_OVA_KEY, lpupdate_key: DEFAULT_LPUPDATE_KEY },
  });

  const save = useMutation({
    mutationFn: (values: DownloadConfigValues) =>
      postWithAuth('admin/download_config', values, 'PATCH'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'downloadConfig'] });
    },
  });

  return (
    <Segment>
      <h4>S3 File Paths</h4>
      <Form onSubmit={handleSubmit((v) => save.mutate(v))} success={save.isSuccess} error={save.isError}>
        <FormField>
          <label>OVA file S3 key</label>
          <Controller
            name="ova_key"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                disabled={isLoading}
                placeholder={DEFAULT_OVA_KEY}
                className="download-config-mono-input"
              />
            )}
          />
        </FormField>
        <FormField>
          <label>Update bundle S3 key</label>
          <Controller
            name="lpupdate_key"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                disabled={isLoading}
                placeholder={DEFAULT_LPUPDATE_KEY}
                className="download-config-mono-input"
              />
            )}
          />
        </FormField>

        {save.isSuccess && <Message success content="File paths saved." />}
        {save.isError && <Message error content="Failed to save file paths." />}

        <Button
          primary
          type="submit"
          loading={save.isPending}
          disabled={save.isPending || isLoading}
        >
          Save
        </Button>
        <Button
          basic
          type="button"
          disabled={save.isPending || isLoading}
          onClick={() => reset(data)}
        >
          Reset
        </Button>
      </Form>
    </Segment>
  );
}
